import { useSuspenseQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useSearchParams } from 'react-router'

import DataTableContainer from '@/components/datatable/components/DataTableContainer'
import CreateNonNylasCalendarLinkButton from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/components/CreateNonNylasCalendarLinkButton'
import CreateOrUpdateReferralLinkModal from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/components/CreateOrUpdateReferralLinkModal'
import { REFERRAL_LINKS_TABLE } from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/constants'
import ExternalLink from '@/components/misc/ExternalLink'
import ReferralLinksTable from '@/components/misc/ReferralLinksTable/ReferralLinksTable'
import { Button } from '@/components/ui/button'
import { REFERRALS_TAB } from '@/lib/constants'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { ROUTES } from '@/lib/routes'
import referralLinkService from '@/lib/services/referralLinkService'
import { GetReferralLinksParams } from '@/types/requests/referral-links'
import { CompanyProfileItemCompany } from '@/types/responses/companies'

interface Props {
  company: CompanyProfileItemCompany
}

const CalendarLinksTab: React.FC<Props> = ({ company }) => {
  const [isCreatingLink, setIsCreatingLink] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(
    searchParams.get(REFERRAL_LINKS_TABLE.searchParams.page) || 1,
  )

  const params: GetReferralLinksParams = {
    'toCompany.id': company.id,
    page,
    pageSize: REFERRAL_LINKS_TABLE.pageSize,
  }

  const referralLinksQuery = useSuspenseQuery({
    queryKey: QUERY_KEYS.referralLinks(params),
    queryFn: () => referralLinkService.getReferralLinks(params),
    staleTime: 5000,
  })

  const referralLinksData = referralLinksQuery.data
  const totalNumReferralLinks = referralLinksData['hydra:totalItems']

  const shouldShowCreateMultiIndividualLink =
    company.numUsersWithCalendarConnection > 1 && !isCreatingLink

  return (
    <div className="max-w-5xl">
      <div className="mb-2">
        <p className="mb-2">
          Use these compliant shareable calendar links and enable your clients
          to book meetings with {company.presentationName} directly. Once a
          client schedules a meeting, the referral will become available from
          your{' '}
          <ExternalLink
            href={ROUTES.referrals({
              searchParams: { tab: REFERRALS_TAB.sent },
            })}
          >
            referrals page
          </ExternalLink>
          .
        </p>

        {company.hasUserWithCalendarConnection ? (
          <>
            <p className="mb-4">
              You'll find a calendar link for all users of{' '}
              {company.presentationName} who have connected their calendars.
              {shouldShowCreateMultiIndividualLink && (
                <>
                  {' '}
                  <Button
                    variant="plain"
                    onClick={() => setIsCreatingLink(true)}
                  >
                    Click here
                  </Button>{' '}
                  to create a calendar link for multiple individuals.
                </>
              )}
            </p>

            {isCreatingLink && (
              <CreateOrUpdateReferralLinkModal
                company={company}
                onCreate={() => setIsCreatingLink(false)}
                onHide={() => setIsCreatingLink(false)}
              />
            )}
          </>
        ) : (
          <CreateNonNylasCalendarLinkButton
            company={company}
            numReferralLinks={totalNumReferralLinks}
          />
        )}
      </div>

      {totalNumReferralLinks > 0 && (
        <DataTableContainer isLoading={referralLinksQuery.isRefetching}>
          <ReferralLinksTable
            referralLinks={referralLinksData['hydra:member']}
            totalReferralLinks={referralLinksData['hydra:totalItems']}
            activePage={page}
            onPageChange={(page) => {
              searchParams.set(
                REFERRAL_LINKS_TABLE.searchParams.page,
                page.toString(),
              )
              setSearchParams(searchParams)
            }}
          />
        </DataTableContainer>
      )}
    </div>
  )
}

export default CalendarLinksTab
